// Thin fetch wrapper around the ClearanceConnect REST API.
import { getStoredToken } from './authStore';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000';

async function request(path, { method = 'GET', body, auth = false } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const token = getStoredToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new Error(
      `Could not reach the API at ${API_BASE}. Is the backend running? (${err.message})`
    );
  }

  if (res.status === 204) return null;

  const rawText = await res.text();
  let data = null;
  if (rawText) {
    try {
      data = JSON.parse(rawText);
    } catch {
      // Body wasn't valid JSON — most commonly this means the request never
      // reached the API at all (e.g. VITE_API_URL is wrong and the request
      // landed on the frontend's own SPA shell, or a proxy/ad-blocker
      // returned an HTML page instead of JSON). Surface that clearly instead
      // of silently returning null, which would crash callers that expect
      // an object back.
      throw new Error(
        `Expected JSON from ${API_BASE}${path} but got something else (status ${res.status}). ` +
          `Check that VITE_API_URL points at your running backend.`
      );
    }
  }

  if (!res.ok) {
    throw new Error(data?.error || `Request failed with status ${res.status}`);
  }

  return data;
}

// ─── Auth ──────────────────────────────────────────────────
export const authApi = {
  // Registration — 2 steps: submit details (sends an email OTP) → verify
  // the code → wait for admin approval before login is possible.
  registerStart: (payload) => request('/api/auth/register', { method: 'POST', body: payload }),
  registerVerify: (payload) => request('/api/auth/register/verify', { method: 'POST', body: payload }),
  registerResend: (payload) => request('/api/auth/register/resend', { method: 'POST', body: payload }),
  login: (payload) => request('/api/auth/login', { method: 'POST', body: payload }),
  me: () => request('/api/auth/me', { auth: true }),
  // Admin — two-step login with email OTP MFA.
  adminLoginStart: (payload) => request('/api/auth/admin/login', { method: 'POST', body: payload }),
  adminVerifyOtp: (payload) => request('/api/auth/admin/verify-otp', { method: 'POST', body: payload }),
  // Forgot password — 2 steps: request a code by email → verify code + set a new password.
  forgotPasswordStart: (payload) => request('/api/auth/forgot-password', { method: 'POST', body: payload }),
  forgotPasswordResend: (payload) => request('/api/auth/forgot-password/resend', { method: 'POST', body: payload }),
  forgotPasswordReset: (payload) => request('/api/auth/forgot-password/reset', { method: 'POST', body: payload }),
};

// ─── Listings ──────────────────────────────────────────────
export const listingsApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v))
    ).toString();
    return request(`/api/listings${qs ? `?${qs}` : ''}`);
  },
  mine: () => request('/api/listings/mine', { auth: true }),
  get: (id) => request(`/api/listings/${id}`, { auth: true }),
  related: (id) => request(`/api/listings/${id}/related`),
  byIds: (ids) => request('/api/listings/by-ids', { method: 'POST', body: { ids } }),
  categories: () => request('/api/listings/meta/categories'),
  create: (payload) => request('/api/listings', { method: 'POST', body: payload, auth: true }),
  update: (id, payload) => request(`/api/listings/${id}`, { method: 'PUT', body: payload, auth: true }),
  remove: (id) => request(`/api/listings/${id}`, { method: 'DELETE', auth: true }),
};

// ─── Admin — full authority over sellers, buyers, listings, orders ─────────
export const adminApi = {
  stats: () => request('/api/admin/stats', { auth: true }),
  users: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v))
    ).toString();
    return request(`/api/admin/users${qs ? `?${qs}` : ''}`, { auth: true });
  },
  userDetail: (id) => request(`/api/admin/users/${id}`, { auth: true }),
  suspendUser: (id, suspended) =>
    request(`/api/admin/users/${id}/suspend`, { method: 'PUT', body: { suspended }, auth: true }),
  setUserApproval: (id, approvalStatus) =>
    request(`/api/admin/users/${id}/approval`, { method: 'PUT', body: { approvalStatus }, auth: true }),
  verifyUser: (id, verified) =>
    request(`/api/admin/users/${id}/verify`, { method: 'PUT', body: { verified }, auth: true }),
  deleteUser: (id) => request(`/api/admin/users/${id}`, { method: 'DELETE', auth: true }),
  listings: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v))
    ).toString();
    return request(`/api/admin/listings${qs ? `?${qs}` : ''}`, { auth: true });
  },
  moderateListing: (id, adminStatus, adminNotes) =>
    request(`/api/admin/listings/${id}/moderate`, { method: 'PUT', body: { adminStatus, adminNotes }, auth: true }),
  featureListing: (id, featured) =>
    request(`/api/admin/listings/${id}/feature`, { method: 'PUT', body: { featured }, auth: true }),
  deleteListing: (id) => request(`/api/admin/listings/${id}`, { method: 'DELETE', auth: true }),
  orders: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v))
    ).toString();
    return request(`/api/admin/orders${qs ? `?${qs}` : ''}`, { auth: true });
  },
};

// ─── Orders ────────────────────────────────────────────────
export const ordersApi = {
  list: () => request('/api/orders', { auth: true }),
  quote: (payload) => request('/api/orders/quote', { method: 'POST', body: payload, auth: true }),
  create: (payload) => request('/api/orders', { method: 'POST', body: payload, auth: true }),
  verifyPayment: (id, payload) =>
    request(`/api/orders/${id}/verify-payment`, { method: 'POST', body: payload, auth: true }),
  // `extra` carries { trackingNumber, courierName } — required when status is 'in_transit'.
  advanceStatus: (id, status, extra = {}) =>
    request(`/api/orders/${id}/status`, { method: 'PUT', body: { status, ...extra }, auth: true }),
  updateTracking: (id, payload) =>
    request(`/api/orders/${id}/tracking`, { method: 'PUT', body: payload, auth: true }),
};

// ─── Negotiations — capped bulk-buyer price negotiation ─────
export const negotiationsApi = {
  create: (payload) => request('/api/negotiations', { method: 'POST', body: payload, auth: true }),
  mine: () => request('/api/negotiations/mine', { auth: true }),
  received: () => request('/api/negotiations/received', { auth: true }),
  respond: (id, action, counterPrice) =>
    request(`/api/negotiations/${id}/respond`, { method: 'PUT', body: { action, counterPrice }, auth: true }),
  respondToCounter: (id, action) =>
    request(`/api/negotiations/${id}/counter-response`, { method: 'PUT', body: { action }, auth: true }),
};

// ─── Addresses — buyer's saved shipping addresses (up to 3) ─
export const addressesApi = {
  list: () => request('/api/addresses', { auth: true }),
  create: (payload) => request('/api/addresses', { method: 'POST', body: payload, auth: true }),
  update: (id, payload) => request(`/api/addresses/${id}`, { method: 'PUT', body: payload, auth: true }),
  setDefault: (id) => request(`/api/addresses/${id}/default`, { method: 'PUT', auth: true }),
  remove: (id) => request(`/api/addresses/${id}`, { method: 'DELETE', auth: true }),
};

// ─── Stats ─────────────────────────────────────────────────
export const statsApi = {
  platform: () => request('/api/stats/platform'),
  gmvTrend: () => request('/api/stats/gmv-trend'),
  categoryBreakdown: () => request('/api/stats/category-breakdown'),
};
